import type { ReactNode } from 'react';
import { Alert, Button, Flex, Tag, Tooltip, Typography, theme } from 'antd';
import {
  CheckCircleOutlined,
  DislikeFilled,
  DislikeOutlined,
  LikeFilled,
  LikeOutlined,
  LoadingOutlined,
  MinusCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';
import { isLive, type CardPart, type Part, type Turn } from '../chatReducer';
import { buildCrewSteps } from '../crewSteps';
import { buildImportSteps } from '../importSteps';
import type { CardAction, Rating } from '../types';
import { agentLabel } from '../display';
import { GENERIC_FAILURE } from '../wire';
import CitationChips from './CitationChips';
import CrewSteps from './CrewSteps';
import PackCard from './PackCard';
import PricingCard from './PricingCard';
import StartCard from './StartCard';

/** A tool step still `started` after its turn ended was cut short: show it stopped, not spinning. */
const stepIcon = (
  status: 'started' | 'done' | 'error',
  live: boolean,
): ReactNode => {
  if (status === 'done') return <CheckCircleOutlined />;
  if (status === 'error') return <WarningOutlined />;
  return live ? <LoadingOutlined /> : <MinusCircleOutlined />;
};

type CardHandlers = {
  onAct: (
    card: CardPart,
    action: CardAction,
    reason?: string,
    selected?: string[],
  ) => void;
  onRunAgain: () => void;
  /** A run is streaming: Start and Run again wait for it. */
  busy: boolean;
};

const PartView = ({
  part,
  live,
  onAct,
  onRunAgain,
  busy,
}: { part: Part; live: boolean } & CardHandlers) => {
  switch (part.kind) {
    case 'text':
      return (
        <div>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {agentLabel(part.agent)}
          </Typography.Text>
          {/* Model text is untrusted: markdown only, sanitised, with no plugin that
              allows raw HTML, and no images, which would fetch a URL the model
              chose. The server already wrote the [n] citation markers; they
              stay plain text and the chips below are numbered from the
              citation events. */}
          <ReactMarkdown
            rehypePlugins={[rehypeSanitize]}
            disallowedElements={['img']}
            components={{
              a: ({ href, children }) => (
                <a href={href} target="_blank" rel="noopener noreferrer">
                  {children}
                </a>
              ),
            }}
          >
            {part.text}
          </ReactMarkdown>
        </div>
      );
    case 'tool_step': {
      const stopped = part.status === 'started' && !live;
      return (
        <Tooltip title={stopped ? `${part.name} (stopped)` : part.name}>
          <Typography.Text
            type={part.status === 'error' ? 'warning' : 'secondary'}
            style={{ fontSize: 12 }}
          >
            {stepIcon(part.status, live)}{' '}
            {part.agent === 'assistant' ? '' : `${agentLabel(part.agent)} · `}
            {part.summary || part.name}
          </Typography.Text>
        </Tooltip>
      );
    }
    case 'crew_progress':
    case 'import_progress':
      return null; // drawn by the steps strip
    case 'approval_card':
      if (part.cardKind === 'start_confirm') {
        return (
          <StartCard
            card={part}
            busy={busy}
            onAct={(action) => onAct(part, action)}
          />
        );
      }
      if (part.cardKind === 'pack' && part.pack) {
        return (
          <PackCard
            card={part}
            pack={part.pack}
            busy={busy}
            onAct={(action, reason) => onAct(part, action, reason)}
            onRunAgain={onRunAgain}
          />
        );
      }
      if (part.cardKind === 'pricing_update' && part.pricing) {
        return (
          <PricingCard
            card={part}
            update={part.pricing}
            busy={busy}
            onAct={(action, opts) =>
              onAct(part, action, opts.reason, opts.selected)
            }
          />
        );
      }
      return (
        <Alert
          type="warning"
          showIcon
          message={part.title || 'Approval needed'}
          description="This card can't be shown in this build."
        />
      );
  }
};

type TurnViewProps = {
  turn: Turn;
  onRate: (rating: Rating) => void;
} & CardHandlers;

const TurnView = ({ turn, onRate, onAct, onRunAgain, busy }: TurnViewProps) => {
  const { token } = theme.useToken();
  const live = isLive(turn.status);
  const importSteps = buildImportSteps(turn.parts, live);
  const steps =
    importSteps.length > 0 ? importSteps : buildCrewSteps(turn.parts, live);

  return (
    <div style={{ padding: '12px 0' }}>
      <Flex justify="flex-end">
        <div
          style={{
            maxWidth: '85%',
            padding: '8px 12px',
            borderRadius: 12,
            background: token.colorFillSecondary,
            whiteSpace: 'pre-wrap',
            overflowWrap: 'anywhere',
          }}
        >
          {turn.userText}
        </div>
      </Flex>

      <Flex vertical gap={6} style={{ marginTop: 8 }}>
        {turn.draftSkills.length > 0 && (
          <div>
            <Tag color="gold" style={{ whiteSpace: 'normal' }}>
              {`Using your draft of ${turn.draftSkills.map((n) => `\`${n}\``).join(', ')}`}
            </Tag>
          </div>
        )}
        {steps && <CrewSteps steps={steps} />}
        {turn.parts.map((part, i) => (
          <PartView
            key={i}
            part={part}
            live={live}
            onAct={onAct}
            onRunAgain={onRunAgain}
            busy={busy}
          />
        ))}
        {turn.citations.length > 0 && (
          <CitationChips citations={turn.citations} />
        )}
        {live && (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            <LoadingOutlined />{' '}
            {turn.status === 'reconnecting' ? 'Reconnecting…' : 'Thinking…'}
          </Typography.Text>
        )}
        {turn.status === 'error' && (
          <Alert
            type="error"
            showIcon
            message={turn.error || GENERIC_FAILURE}
            description={
              turn.runId ? (
                <Typography.Text
                  type="secondary"
                  copyable={{ text: turn.runId }}
                >
                  {`Run ${turn.runId}`}
                </Typography.Text>
              ) : undefined
            }
          />
        )}
        {turn.status === 'done' && turn.runId && (
          <Flex gap={4}>
            <Tooltip title="Helpful">
              <Button
                type="text"
                size="small"
                aria-label="Helpful"
                icon={turn.rating === 'up' ? <LikeFilled /> : <LikeOutlined />}
                onClick={() => onRate('up')}
              />
            </Tooltip>
            <Tooltip title="Not helpful">
              <Button
                type="text"
                size="small"
                aria-label="Not helpful"
                icon={
                  turn.rating === 'down' ? (
                    <DislikeFilled />
                  ) : (
                    <DislikeOutlined />
                  )
                }
                onClick={() => onRate('down')}
              />
            </Tooltip>
          </Flex>
        )}
      </Flex>
    </div>
  );
};

export default TurnView;
